import { IStoragePoolConfig } from "api/entities/deployment-job";
import {
  IDefaultShardCluster,
  IDefaultShardClusterSection,
} from "api/entities/shard-cluster";
import { IShardRepositoryDeployRequest } from "api/entities/shard-repository";
import { IStorageNodeProviderListItem } from "api/entities/storage-node-providers";
import { shardClusterService } from "api/services/shard-cluster-service";
import { storageNodeProviderService } from "api/services/storage-node-providers";
import { StorageReconstructionSection } from "components/shard-clusters/sections/storage-reconstruction-section/storage-reconstruction-section";
import { CompactTable } from "components/shared-ui/compact-table/compact-table";
import Checkbox from "components/shared-ui/input/checkbox";
import { Select } from "components/shared-ui/select";
import { IColumnDefinition } from "components/shared-ui/table/table";
import { CONFIG } from "config/config";
import { ProviderRegionMap } from "helper/regions";
import React, { useCallback, useEffect, useState } from "react";
import { DeploymentProviders } from "utils/deployment-providers-util";
import { IFormField, useField } from "utils/form-utils";
import { useApi } from "utils/hooks/use-api";

export interface IRecommendProps {
  bucketName: string;
  onChange: (config: IStoragePoolConfig) => void;
}

interface StorageNodeOption extends IShardRepositoryDeployRequest {
  content: boolean;
  key: boolean;
  meta: boolean;
}

function mapDefaultClusterToNodes(
  defaultPool: IDefaultShardCluster,
  bucketName: string,
  getProvider: (id: string) => DeploymentProviders
): StorageNodeOption[] {
  type NodeKey = string; // id-region-index

  const nodeMap = new Map<NodeKey, StorageNodeOption>();
  let bucketIndex = 1;

  // Helper to add nodes from a section
  const addSection = (
    section: IDefaultShardClusterSection,
    sectionName: Extract<keyof StorageNodeOption, "content" | "key" | "meta">
  ) => {
    const occurrenceCount: Record<string, number> = {};

    section.endpoints.forEach((ep) => {
      const baseKey = `${ep.id}-${ep.region}`;
      const index = (occurrenceCount[baseKey] ?? 0) + 1;
      occurrenceCount[baseKey] = index;

      const nodeKey = `${baseKey}-${index}`; // unique per duplicate occurrence

      if (!nodeMap.has(nodeKey)) {
        nodeMap.set(nodeKey, {
          bucket: `${bucketName}-${bucketIndex++}`,
          provider: getProvider(ep.id),
          providerId: ep.id,
          region: ep.region,
          content: false,
          key: false,
          meta: false,
        });
      }

      // Mark the section for this node
      const node = nodeMap.get(nodeKey)!;
      node[sectionName] = true;
    });
  };

  addSection(defaultPool.content, "content");
  addSection(defaultPool.key, "key");
  addSection(defaultPool.meta, "meta");

  return Array.from(nodeMap.values());
}

export const Recommended = (props: IRecommendProps) => {
  const [defaultProviders, setDefaultProviders] =
    useState<IStorageNodeProviderListItem[]>(null);
  const [nodes, setNodes] = useState<StorageNodeOption[]>([]);
  const [defaultCluster, setDefaultCluster] =
    useState<IDefaultShardCluster>(null);

  const contentSecurityLevel = useField<number>(CONFIG.minSecurityLevel, []);
  const keySecurityLevel = useField<number>(CONFIG.minSecurityLevel, []);
  const metaSecurityLevel = useField<number>(CONFIG.minSecurityLevel, []);

  const [nodesCount, setNodesCount] = useState({
    content: CONFIG.minSecurityLevel,
    key: CONFIG.minSecurityLevel,
    meta: CONFIG.minSecurityLevel,
  });

  const [api, isLoading] = useApi();

  const handleChange = (node: StorageNodeOption) => {
    setNodes((prev) => prev.map((p) => (p.bucket === node.bucket ? node : p)));
  };

  const getProviderName = useCallback(
    (id: string, providers?: IStorageNodeProviderListItem[]) => {
      return (providers ?? defaultProviders)?.find(
        (provider) => provider.id === id
      )?.name;
    },
    [defaultProviders]
  );

  const columns: IColumnDefinition<StorageNodeOption, null>[] = [
    {
      testId: "col-provider",
      label: "Provider",
      formatter: (row) => getProviderName(row.providerId),
    },
    {
      testId: "col-region",
      label: "Region",
      formatter: (row) => (
        <SelectRegion
          value={row.region}
          provider={row.provider}
          onChange={(region) => {
            handleChange({ ...row, region: region });
          }}
        />
      ),
    },
    {
      testId: "col-content",
      //   className: styles["col-content"],
      label: "Content",
      formatter: (row) => (
        <Checkbox
          testId="content-checkbox"
          checked={row.content}
          onChange={(value) => {
            handleChange({ ...row, content: value });
          }}
        />
      ),
    },
    {
      testId: "col-key",
      //   className: styles["col-key"],
      label: "Key",
      formatter: (row) => (
        <Checkbox
          testId="key-checkbox"
          checked={row.key}
          onChange={(value) => {
            handleChange({ ...row, key: value });
          }}
        />
      ),
    },
    {
      testId: "col-metadata",
      //   className: styles["col-meta"],
      label: "Metadata",
      formatter: (row) => (
        <Checkbox
          testId="metadata-checkbox"
          checked={row.meta}
          onChange={(value) => {
            handleChange({ ...row, meta: value });
          }}
        />
      ),
    },
  ];

  useEffect(() => {
    api(shardClusterService.getDefault()).then((defaultPool) => {
      setDefaultCluster(defaultPool);
      contentSecurityLevel.init(defaultPool.content.securityLevel);
      keySecurityLevel.init(defaultPool.key.securityLevel);
      metaSecurityLevel.init(defaultPool.meta.securityLevel);

      const uniqueProviders = [
        ...new Set([
          ...defaultPool.content.endpoints.map((ep) => ep.id),
          ...defaultPool.key.endpoints.map((ep) => ep.id),
          ...defaultPool.meta.endpoints.map((ep) => ep.id),
        ]),
      ];

      Promise.all(
        uniqueProviders.map((id) => api(storageNodeProviderService.get(id)))
      ).then((providers) => {
        setDefaultProviders(providers);

        const getProvider = (id: string): DeploymentProviders => {
          return providers.find((provider) => provider.id === id)?.provider;
        };

        const nodes = mapDefaultClusterToNodes(
          defaultPool,
          props.bucketName,
          getProvider
        );

        setNodes(nodes);
      });
    });
  }, []);

  useEffect(() => {
    setNodes((prev) => {
      return prev.map((node, idx) => {
        return { ...node, bucket: `${props.bucketName}-${idx}` };
      });
    });
  }, [props.bucketName]);

  useEffect(() => {
    const contentNodes = nodes.filter(
      (node) => node.content
    ) as IShardRepositoryDeployRequest[];
    const keyNodes = nodes.filter(
      (node) => node.key
    ) as IShardRepositoryDeployRequest[];
    const metaNodes = nodes.filter(
      (node) => node.meta
    ) as IShardRepositoryDeployRequest[];

    setNodesCount({
      content: contentNodes.length,
      key: keyNodes.length,
      meta: metaNodes.length,
    });
    props.onChange({
      name: `${props.bucketName}-shard-cluster`,
      ContentSecurityLevel: contentSecurityLevel.value,
      KeySecurityLevel: keySecurityLevel.value,
      MetaSecurityLevel: metaSecurityLevel.value,
      contentStorages: contentNodes,
      keyStorages: keyNodes,
      metaStorages: metaNodes,
    });
  }, [
    nodes,
    contentSecurityLevel.value,
    keySecurityLevel.value,
    metaSecurityLevel.value,
  ]);

  return (
    <>
      <CompactTable
        testId="provider-nodes-table"
        columns={columns}
        loading={isLoading}
        rows={nodes}
        rowId={(node) => node.bucket}
      />
      <StorageReconstructionSection
        isReadOnly={false}
        contentSecurityLevel={contentSecurityLevel}
        keySecurityLevel={keySecurityLevel}
        metaSecurityLevel={metaSecurityLevel}
        maxContentSecurityLevel={Math.max(
          nodesCount.content - 1,
          CONFIG.minSecurityLevel
        )}
        maxKeySecurityLevel={Math.max(
          nodesCount.key - 1,
          CONFIG.minSecurityLevel
        )}
        maxMetaSecurityLevel={Math.max(
          nodesCount.meta - 1,
          CONFIG.minSecurityLevel
        )}
        contentRedundancyLevel={0}
        keyRedundancyLevel={nodesCount.key}
        metaRedundancyLevel={nodesCount.meta}
      />
    </>
  );
};

interface ISelectRegionProps {
  onChange: (region: string) => void;
  value: string;
  provider: DeploymentProviders;
}

const SelectRegion = (props: ISelectRegionProps) => {
  return (
    <div>
      <Select
        testId="region-select"
        value={props.value}
        onChange={props.onChange}
        options={ProviderRegionMap[props.provider]}
      />
    </div>
  );
};
